// Dynamic Data Layer: Routes database/auth calls to either live Firebase or local LocalStorage mock fallback
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  updateProfile as firebaseUpdateProfile 
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  addDoc, 
  collection, 
  onSnapshot 
} from 'firebase/firestore';

// -------------------------------------------------------------
// DEFAULT DATA DEFINITIONS
// -------------------------------------------------------------
const DEFAULT_PRODUCTS = [
  {
    id: 'prod_mouse',
    name: 'The Sparkle Mouse',
    description: 'A cute little bead mouse handcrafted with premium metallic purple and pink seed beads.',
    price: 5.00,
    category: 'Bead Animals',
    imageUrl: 'https://images.unsplash.com/photo-1550859970-b568aff7dbe8?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    stock: 12,
    isCustomizable: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'prod_flower',
    name: 'Garden Party Charm',
    description: 'A cheerful glitter flower charm designed with bright yellow and white flower beads.',
    price: 4.00,
    category: 'Charms',
    imageUrl: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    stock: 8,
    isCustomizable: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'prod_lizard',
    name: 'Retro Lizard Pet',
    description: 'The classic 90s bead lizard keychain made with green and black pony beads.',
    price: 6.00,
    category: 'Bead Animals',
    imageUrl: 'https://images.unsplash.com/photo-1590076214667-c06f2348b6bb?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    stock: 15,
    isCustomizable: false,
    createdAt: new Date().toISOString()
  }
];

const DEFAULT_USERS = [
  {
    uid: 'user_admin',
    email: 'admin@beadboutique.com',
    displayName: 'Georgia',
    role: 'admin',
    password: 'admin123',
    shippingAddress: {
      line1: '123 Crafter Lane',
      city: 'St. Peters',
      state: 'MO',
      postal_code: '63376'
    }
  },
  {
    uid: 'user_customer',
    email: 'customer@beadboutique.com',
    displayName: 'Larry',
    role: 'customer',
    password: 'customer123',
    shippingAddress: {
      line1: '456 Buyer Boulevard',
      city: 'St. Louis',
      state: 'MO',
      postal_code: '63101'
    }
  }
];

const DEFAULT_DONATIONS = {
  totalDonated: 135.00,
  goalAmount: 1000.00,
  donationPercentage: 0.10
};

// -------------------------------------------------------------
// MODE CONFIGURATION
// -------------------------------------------------------------
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Check if credentials are set to determine mode
export const useFirebase = !!process.env.REACT_APP_FIREBASE_API_KEY;

let app, auth, db;
let cachedProducts = [];
let cachedDonations = { ...DEFAULT_DONATIONS };
let cachedOrders = [];
let cachedQuotes = [];
let currentUser = null;

// Listeners helper for UI triggers
const listeners = new Set();
const notifyListeners = () => listeners.forEach(l => l());

export const subscribeToDB = (callback) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

// -------------------------------------------------------------
// LOCALSTORAGE MOCK FALLBACK STATE & CONFIG (OFFLINE MODE)
// -------------------------------------------------------------
const getStorageItem = (key, defaultValue) => {
  const item = localStorage.getItem(key);
  if (!item) {
    localStorage.setItem(key, JSON.stringify(defaultValue));
    return defaultValue;
  }
  return JSON.parse(item);
};

const setStorageItem = (key, data) => {
  localStorage.setItem(key, JSON.stringify(data));
};

let mockProductsList, mockUsersList, mockDonationsStats, mockOrdersList, mockQuotesList;

if (!useFirebase) {
  mockProductsList = getStorageItem('bb_products', DEFAULT_PRODUCTS);
  mockUsersList = getStorageItem('bb_users', DEFAULT_USERS);
  mockDonationsStats = getStorageItem('bb_donations', DEFAULT_DONATIONS);
  mockOrdersList = getStorageItem('bb_orders', []);
  mockQuotesList = getStorageItem('bb_quotes', []);
  currentUser = getStorageItem('bb_current_user', null);
}

// -------------------------------------------------------------
// INITIALIZE FIREBASE & REAL-TIME LISTENERS (ONLINE MODE)
// -------------------------------------------------------------
if (useFirebase) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);

    // 1. Subscribe to Products
    onSnapshot(collection(db, 'products'), (snapshot) => {
      cachedProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      notifyListeners();
    }, (error) => console.error("Products Sync Error:", error));

    // 2. Subscribe to Donation settings/stats
    onSnapshot(doc(db, 'globals', 'st_jude'), (snap) => {
      if (snap.exists()) {
        cachedDonations = snap.data();
      } else {
        // Seed default stats
        setDoc(doc(db, 'globals', 'st_jude'), DEFAULT_DONATIONS);
      }
      notifyListeners();
    }, (error) => console.error("Donations Sync Error:", error));

    // 3. Subscribe to Orders
    onSnapshot(collection(db, 'orders'), (snapshot) => {
      cachedOrders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      notifyListeners();
    }, (error) => console.error("Orders Sync Error:", error));

    // 4. Subscribe to Quotes
    onSnapshot(collection(db, 'custom_quotes'), (snapshot) => {
      cachedQuotes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      notifyListeners();
    }, (error) => console.error("Quotes Sync Error:", error));

    // 5. Auth State listener
    onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        // Retrieve custom user profile for roles/address
        const profileRef = doc(db, 'profiles', fbUser.uid);
        const profileSnap = await getDoc(profileRef);
        if (profileSnap.exists()) {
          currentUser = { uid: fbUser.uid, email: fbUser.email, ...profileSnap.data() };
        } else {
          // If first sign up didn't write profile (edge case), create fallback profile
          currentUser = { 
            uid: fbUser.uid, 
            email: fbUser.email, 
            displayName: fbUser.displayName || 'User', 
            role: 'customer', 
            shippingAddress: null 
          };
        }
      } else {
        currentUser = null;
      }
      notifyListeners();
    });
  } catch (err) {
    console.error("Firebase Initialization Failed. Falling back to offline mock.", err);
  }
}

// -------------------------------------------------------------
// DYNAMIC IMPLEMENTATION EXPORTS
// -------------------------------------------------------------

export const mockAuth = {
  login: async (email, password) => {
    if (useFirebase) {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Fetch corresponding profile document
      const profSnap = await getDoc(doc(db, 'profiles', userCredential.user.uid));
      currentUser = { 
        uid: userCredential.user.uid, 
        email: userCredential.user.email,
        displayName: userCredential.user.displayName || 'User',
        role: profSnap.exists() ? profSnap.data().role : 'customer',
        shippingAddress: profSnap.exists() ? profSnap.data().shippingAddress : null
      };
      notifyListeners();
      return currentUser;
    } else {
      const user = mockUsersList.find(u => u.email.toLowerCase() === email.toLowerCase() && u.password === password);
      if (!user) throw new Error('Invalid email or password.');
      currentUser = { ...user };
      delete currentUser.password;
      setStorageItem('bb_current_user', currentUser);
      notifyListeners();
      return currentUser;
    }
  },

  register: async (email, password, displayName) => {
    if (useFirebase) {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await firebaseUpdateProfile(userCredential.user, { displayName });
      
      const newProfile = {
        displayName,
        email,
        role: 'customer', // Default role
        shippingAddress: null,
        createdAt: new Date().toISOString()
      };
      // Save profile doc to Firestore
      await setDoc(doc(db, 'profiles', userCredential.user.uid), newProfile);
      
      currentUser = { uid: userCredential.user.uid, email, ...newProfile };
      notifyListeners();
      return currentUser;
    } else {
      const exists = mockUsersList.find(u => u.email.toLowerCase() === email.toLowerCase());
      if (exists) throw new Error('An account with this email already exists.');
      
      const newUser = {
        uid: 'user_' + Math.random().toString(36).substr(2, 9),
        email,
        displayName,
        role: 'customer',
        password,
        shippingAddress: null
      };
      
      mockUsersList.push(newUser);
      setStorageItem('bb_users', mockUsersList);
      
      currentUser = { ...newUser };
      delete currentUser.password;
      setStorageItem('bb_current_user', currentUser);
      notifyListeners();
      return currentUser;
    }
  },

  logout: async () => {
    if (useFirebase) {
      await signOut(auth);
      currentUser = null;
      notifyListeners();
    } else {
      currentUser = null;
      localStorage.removeItem('bb_current_user');
      notifyListeners();
    }
  },

  getCurrentUser: () => currentUser,

  updateProfile: async (displayName, shippingAddress) => {
    if (!currentUser) throw new Error('Not authenticated.');
    
    if (useFirebase) {
      await firebaseUpdateProfile(auth.currentUser, { displayName });
      await updateDoc(doc(db, 'profiles', currentUser.uid), { displayName, shippingAddress });
      currentUser = { ...currentUser, displayName, shippingAddress };
      notifyListeners();
      return currentUser;
    } else {
      mockUsersList = mockUsersList.map(u => {
        if (u.uid === currentUser.uid) {
          return { ...u, displayName, shippingAddress };
        }
        return u;
      });
      setStorageItem('bb_users', mockUsersList);

      currentUser = { ...currentUser, displayName, shippingAddress };
      setStorageItem('bb_current_user', currentUser);
      notifyListeners();
      return currentUser;
    }
  }
};

export const mockProducts = {
  getProducts: () => {
    return useFirebase ? cachedProducts : mockProductsList;
  },
  
  addProduct: async (productData) => {
    if (useFirebase) {
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        createdAt: new Date().toISOString()
      });
      return { id: docRef.id, ...productData };
    } else {
      const newProduct = {
        id: 'prod_' + Math.random().toString(36).substr(2, 9),
        ...productData,
        createdAt: new Date().toISOString()
      };
      mockProductsList.push(newProduct);
      setStorageItem('bb_products', mockProductsList);
      notifyListeners();
      return newProduct;
    }
  },

  updateProduct: async (updatedProduct) => {
    if (useFirebase) {
      const { id, ...data } = updatedProduct;
      await updateDoc(doc(db, 'products', id), data);
      return updatedProduct;
    } else {
      mockProductsList = mockProductsList.map(p => p.id === updatedProduct.id ? updatedProduct : p);
      setStorageItem('bb_products', mockProductsList);
      notifyListeners();
      return updatedProduct;
    }
  },

  deleteProduct: async (id) => {
    if (useFirebase) {
      await deleteDoc(doc(db, 'products', id));
    } else {
      mockProductsList = mockProductsList.filter(p => p.id !== id);
      setStorageItem('bb_products', mockProductsList);
      notifyListeners();
    }
  }
};

export const mockDonations = {
  getDonationStats: () => {
    return useFirebase ? cachedDonations : mockDonationsStats;
  },

  updateDonationStats: async (updatedDonations) => {
    if (useFirebase) {
      await updateDoc(doc(db, 'globals', 'st_jude'), updatedDonations);
      return { ...cachedDonations, ...updatedDonations };
    } else {
      mockDonationsStats = { ...mockDonationsStats, ...updatedDonations };
      setStorageItem('bb_donations', mockDonationsStats);
      notifyListeners();
      return mockDonationsStats;
    }
  }
};

export const mockOrders = {
  getOrders: (userId = null) => {
    if (useFirebase) {
      if (userId) {
        return cachedOrders.filter(o => o.userId === userId);
      }
      return cachedOrders;
    } else {
      if (userId) {
        return mockOrdersList.filter(o => o.userId === userId);
      }
      return mockOrdersList;
    }
  },

  createOrder: async (cartItems, totalAmount) => {
    if (!currentUser) throw new Error('Log in to check out.');
    
    const donationRate = useFirebase ? cachedDonations.donationPercentage : mockDonationsStats.donationPercentage;
    const donationAmount = totalAmount * donationRate;
    
    const newOrder = {
      userId: currentUser.uid,
      customerName: currentUser.displayName,
      customerEmail: currentUser.email,
      items: cartItems,
      totalAmount,
      donationAmount,
      status: 'processing',
      createdAt: new Date().toISOString()
    };

    if (useFirebase) {
      // 1. Create order document
      await addDoc(collection(db, 'orders'), newOrder);
      
      // 2. Increment global donations
      await updateDoc(doc(db, 'globals', 'st_jude'), {
        totalDonated: cachedDonations.totalDonated + donationAmount
      });

      // 3. Subtract stock for non-custom catalog pieces
      for (const item of cartItems) {
        const catalogProd = cachedProducts.find(p => p.id === item.id);
        if (catalogProd) {
          const newStock = Math.max(0, catalogProd.stock - item.quantity);
          await updateDoc(doc(db, 'products', item.id), { stock: newStock });
        }
      }
      return newOrder;
    } else {
      newOrder.id = 'ord_' + Math.random().toString(36).substr(2, 9).toUpperCase();
      mockOrdersList.unshift(newOrder);
      setStorageItem('bb_orders', mockOrdersList);

      // Adjust stock for non-custom items
      cartItems.forEach(item => {
        const catalogProd = mockProductsList.find(p => p.id === item.id);
        if (catalogProd) {
          catalogProd.stock = Math.max(0, catalogProd.stock - item.quantity);
        }
      });
      setStorageItem('bb_products', mockProductsList);

      // Update the running donation total
      mockDonationsStats.totalDonated += donationAmount;
      setStorageItem('bb_donations', mockDonationsStats);

      notifyListeners();
      return newOrder;
    }
  }
};

export const mockQuotes = {
  getQuotes: (userId = null) => {
    if (useFirebase) {
      if (userId) {
        return cachedQuotes.filter(q => q.userId === userId);
      }
      return cachedQuotes;
    } else {
      if (userId) {
        return mockQuotesList.filter(q => q.userId === userId);
      }
      return mockQuotesList;
    }
  },

  createQuoteRequest: async (description, customText, customColors, originalProductId = null) => {
    if (!currentUser) throw new Error('Log in to request custom quotes.');

    const newQuote = {
      userId: currentUser.uid,
      customerName: currentUser.displayName,
      customerEmail: currentUser.email,
      description,
      customText,
      customColors,
      originalProductId,
      status: 'pending',
      quotedPrice: null,
      createdAt: new Date().toISOString()
    };

    if (useFirebase) {
      await addDoc(collection(db, 'custom_quotes'), newQuote);
      return newQuote;
    } else {
      newQuote.id = 'qte_' + Math.random().toString(36).substr(2, 9).toUpperCase();
      mockQuotesList.unshift(newQuote);
      setStorageItem('bb_quotes', mockQuotesList);
      notifyListeners();
      return newQuote;
    }
  },

  updateQuoteStatus: async (quoteId, status, quotedPrice = null) => {
    if (useFirebase) {
      const updateData = { status };
      if (quotedPrice !== null) {
        updateData.quotedPrice = parseFloat(quotedPrice);
      }
      await updateDoc(doc(db, 'custom_quotes', quoteId), updateData);
    } else {
      mockQuotesList = mockQuotesList.map(q => {
        if (q.id === quoteId) {
          return { 
            ...q, 
            status, 
            quotedPrice: quotedPrice !== null ? parseFloat(quotedPrice) : q.quotedPrice 
          };
        }
        return q;
      });
      setStorageItem('bb_quotes', mockQuotesList);
      notifyListeners();
    }
  }
};
